/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import {AngularModalService} from '../../common/modal/angular-modal.service';
import template from './marketplace-product-edit-page.component.html';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {ADN_TARGETING_TYPES} from '../../targeting/targeting-constants';
import {TargetingEditorModalService} from '../../targeting/targeting-editor-modal.service';
import {LocalNetworkInfo} from '../../../components/session/local-network-info';
import {LayoutSelectorModalComponent} from '../product/layout-selector-modal.component';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {LayoutResource} from '../../../components/api/resources/resource/layout.resource';
import {LineItemResource} from '../../../components/api/resources/resource/line-item.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {visiblePageNumbers} from '../../common/directives/pagination/visible-page-numbers';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import {compareById} from '../../common/util/object-id-comparison';

function allowedDims(theModel: any): any[] {
  return _.filter(theModel.allowedDimensions, function(dims) {
    return dims[0] >= 0 && dims[1] >= 0;
  });
}

function normalizeResourceIds(items: any[]): string[] {
  return _.compact(_.map(items || [], (item: any) => _.isString(item) ? item : item.id));
}

function ownerTeamId(model: any): string | undefined {
  const ownerTeam = _.get(model, 'ownerTeam');
  return _.isString(ownerTeam) ? ownerTeam : _.get(ownerTeam, 'id');
}

@Component({
  standalone: false,
  selector: 'adn-marketplace-product-edit-page',
  template
})
export class MarketplaceProductEditPageComponent extends AdminResourceEditPageBase {
  allTeams: any[] = [];
  allPublisherTeams: any[] = [];
  canUpdateProduct = false;
  dynamicBiddingAllowed = false;
  hasLayouts = false;
  layoutResult: any = {};
  lineItemColumns = ['name', 'order', 'userState', 'executionState'];
  lineItemProducts: any;
  /** Snapshot — list-helper replaces lineItemProducts.items mid-CD (NG0100). */
  displayLineItems: any[] = [];
  hasLineItems = false;
  listPageSize = 25;
  listTotalCount = 0;
  listCurrentPage = 1;
  ownership = 'NETWORK';
  rateLimitTypes: string[] = [];
  selSmoothingOption = '';
  showOwnership = false;
  showPolitical = false;
  targetingResult: any = {};
  teamsWarning = false;
  tiers: any[] = [];
  targetingTypes = _.map(ADN_TARGETING_TYPES, function(val) {
    return val;
  });

  readonly smoothingOptions = [
    {id: 'optionEf', vals: ['EVEN', 'FRONT_LOADED']},
    {id: 'optionEfs', vals: []},
    {id: 'optionEfcc', vals: ['EVEN', 'FRONT_LOADED', 'CAPPED', 'CATCH_UP']},
    {id: 'optionEfccg', vals: ['EVEN', 'FRONT_LOADED', 'CAPPED', 'CATCH_UP', 'GREEDY']},
    {id: 'optionEfcg', vals: ['EVEN', 'FRONT_LOADED', 'CATCH_UP', 'GREEDY']},
    {id: 'optionUef', vals: ['FAST_AS_POSSIBLE', 'EVEN', 'FRONT_LOADED']},
    {id: 'optionUefs', vals: ['FAST_AS_POSSIBLE', 'EVEN', 'FRONT_LOADED']},
    {id: 'optionE', vals: ['EVEN']},
    {id: 'optionC', vals: ['CAPPED']},
    {id: 'optionF', vals: ['FAST_AS_POSSIBLE']}
  ];

  private readonly defaultSmoothingOption = {id: 'optionEfs', vals: []};

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    @Inject(LineItemResource) private LineItem: any,
    @Inject(TeamResource) private Team: any,
    @Inject(LayoutResource) private Layout: any,
    private adnListHelper: AdnListHelperService,
    private angularModalService: AngularModalService,
    private targetingEditorModalService: TargetingEditorModalService,
    private changeDetectorRef: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);

    this.rateLimitTypes = ['RENDERED_IMPRESSION', 'VISIBLE_IMPRESSION', 'VIEWABLE_IMPRESSION', 'BUDGET'];
    if (LocalNetworkProfile.impsOnAxProductRateLimits()) {
      this.rateLimitTypes.unshift('IMPRESSION');
    }

    this.dynamicBiddingAllowed = LocalNetworkProfile.isMarketplacePlatform();
    this.showPolitical = LocalNetworkProfile.isPureMarketplacePlatform();
    // note we can't check AX_PUBLISHER_TEAM_ADMIN because we don't know the ownerTeam as yet so it is checked
    // in updateCanUpdateProduct
    this.canUpdateProduct = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN');
    this.showOwnership = LocalUserPermissions.hasNetwork('MANAGE_SYSTEM');

    Team.query({minimal: true, filterBy: 'type', filterByValue: 'AX_ADVERTISER'}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.allTeams = page.results;
      });
    });

    Team.query({minimal: true, filterBy: 'type', filterByValue: 'AX_PUBLISHER'}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.allPublisherTeams = page.results;
        this.resolveOwnerTeamReference();
      });
    });
  }

  get lineItemsResolved(): any {
    return this.isNew || this.lineItemProducts?.query?.$resolved;
  }

  get lineItemPageNumbers(): number[] {
    return visiblePageNumbers(this.listCurrentPage, this.lineItemTotalPages());
  }

  protected afterInitEditingModel(): void {
    this.updateCanUpdateProduct();
    this.resolveOwnerTeamReference();
    this.hasLayouts = false;
    if (this.editingModel.layouts && this.editingModel.layouts.length) {
      this.Layout.query(_.map(this.editingModel.layouts, 'id'), (page: any) => {
        // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
        deferMarkForCheck(this.changeDetectorRef, () => {
          this.editingModel.layouts = page.results;
          this.hasLayouts = true;
        });
      });
    } else {
      // false → true flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.changeDetectorRef, () => {
        this.hasLayouts = true;
      });
    }

    this.editingModel.layouts = this.editingModel.layouts || [];
    this.editingModel.type = this.editingModel.type || 'STANDARD';
    this.editingModel.cpmSpecification = this.editingModel.cpmSpecification || 'UNCAPPED';
    this.editingModel.budgetSpecification = this.editingModel.budgetSpecification || 'ALL_OBJECTIVES';
    this.editingModel.lineItemWorkflowType = this.editingModel.lineItemWorkflowType || 'DEFAULT';
    this.editingModel.objectiveSpecification = this.editingModel.objectiveSpecification || 'ANY';
    this.editingModel.bidStrategyOption = this.editingModel.bidStrategyOption || 'STANDARD_ONLY';
    this.editingModel.teams = normalizeResourceIds(this.editingModel.teams);
    this.editingModel.allowedDimensions = _.isArray(this.editingModel.allowedDimensions) ? this.editingModel.allowedDimensions : [[]];
    this.editingModel.allowedDimensions[0] = _.isArray(this.editingModel.allowedDimensions[0]) ? this.editingModel.allowedDimensions[0] : [];
    this.editingModel.targeting = this.editingModel.targeting || {};
    this.editingModel.optionalTargetTypes = this.editingModel.optionalTargetTypes || [];

    const enabledTiers = this.LocalNetworkProfile.get('tiers') || [];
    const currentTier = this.editingModel.defaultTier;
    this.tiers = _.cloneDeep(enabledTiers);
    if (currentTier && !_.find(this.tiers, (tier: any) => tier.id === _.get(currentTier, 'id'))) {
      this.tiers.push(currentTier);
    }
    _.forEach(this.tiers, (tier: any) => {
      tier.displayName = tier.name + (_.find(enabledTiers, {id: tier.id}) ? '' : ' (disabled)');
    });
  }

  protected afterInitAfterSave(data?: any): void {
    this.ownership = this.editingModel.ownerTeam ? 'SPECIFIC_TEAM' : 'NETWORK';
    this.resolveOwnerTeamReference();
    this.editingModel.teams = normalizeResourceIds(_.get(data || this.editingModel, 'teams') || []);
    this.teamsWarning = !!data && (this.editingModel.teams || []).length !== (_.get(data, 'teams') || []).length;
    this.updateCanUpdateProduct();
    this.selSmoothingOption = (_.find(this.smoothingOptions, (option) => {
      return option.vals.join(', ') === (this.editingModel.smoothingChoices || []).join(', ');
    }) || this.defaultSmoothingOption).id;
    this.targetingResult = {data: false};
    this.layoutResult = {data: false};

    if (!this.isNew && !this.lineItemProducts) {
      this.lineItemProducts = {};
      this.adnListHelper.setUpBasicList(this.lineItemProducts, this.LineItem, '', {
        baseTable: 'lineItem',
        filterBy: 'executionState',
        filterByValue: 'RUNNING',
        moreParams: {
          axProduct: this.editingModel.id
        },
        afterFill: () => {
          deferMarkForCheck(this.changeDetectorRef, () => {
            this.displayLineItems = (this.lineItemProducts.items || []).slice();
            this.hasLineItems = this.displayLineItems.length > 0;
            this.listPageSize = this.lineItemProducts.pageSize || 25;
            this.listTotalCount = this.lineItemProducts.totalCount || 0;
            this.listCurrentPage = this.lineItemProducts.currentPage || 1;
          });
        }
      });
    }
  }

  protected onBeforeSubmit(): void {
    this.editingModel.allowedDimensions = allowedDims(this.editingModel);
    if (this.ownership === 'NETWORK') {
      this.editingModel.ownerTeam = null;
    }
    if (this.isNew) {
      LocalNetworkInfo.obtain().increment('axProductsCount');
    }
    this.editingModel.smoothingChoices = (_.find(this.smoothingOptions, (option) => {
      return option.id === this.selSmoothingOption;
    }) || this.defaultSmoothingOption).vals;
  }

  editsMade(): void {
    this.markExternalFormDirty();
  }

  addDimensions(): void {
    this.editingModel.allowedDimensions = this.editingModel.allowedDimensions || [];
    this.editingModel.allowedDimensions.push([]);
    this.markExternalFormDirty();
  }

  removeDimensions(index: number): void {
    _.pullAt(this.editingModel.allowedDimensions, index);
    if (this.editingModel.allowedDimensions.length < 1) {
      this.editingModel.allowedDimensions[0] = [];
    }
    this.markExternalFormDirty();
  }

  editTarget(targeting: any): void {
    this.targetingResult = {data: false};
    this.targetingEditorModalService.open({
      modelLineItem: _.cloneDeep(this.editingModel.data || {}),
      lineItemType: this.editingModel.type === 'POLITICAL' ? 'POLITICAL' : 'NORMAL',
      pageType: 'marketplaceProduct',
      reachEnabled: undefined,
      mandatoryTargets: undefined,
      availableTargets: null,
      overridableTargeting: undefined,
      targetingMode: null,
      targeting,
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(targeting, wc.wc);
      this.editingModel.targeting = _.cloneDeep(targeting);
      this.model.targeting = _.cloneDeep(targeting);
      if (!this.isNew) {
        this.model.savePartial('targeting').then((respObj: any) => {
          this.ngZone.run(() => {
            this.targetingResult.data = true;
            _.set(this.editingModel, 'validationWarnings', respObj.validationWarnings);
          });
        });
      } else {
        this.markExternalFormDirty();
      }
    });
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.allTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  toggleTeam(teamId: string): void {
    this.editingModel.teams = this.editingModel.teams || [];
    const idx = this.editingModel.teams.indexOf(teamId);
    if (idx > -1) {
      this.editingModel.teams.splice(idx, 1);
    } else {
      this.editingModel.teams.push(teamId);
    }
    this.markExternalFormDirty();
  }

  hasTeam(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleOptionalTargetType(targetType: string): void {
    this.editingModel.optionalTargetTypes = this.editingModel.optionalTargetTypes || [];
    const idx = this.editingModel.optionalTargetTypes.indexOf(targetType);
    if (idx > -1) {
      this.editingModel.optionalTargetTypes.splice(idx, 1);
    } else {
      this.editingModel.optionalTargetTypes.push(targetType);
    }
    this.markExternalFormDirty();
  }

  hasOptionalTargetType(targetType: string): boolean {
    return (this.editingModel.optionalTargetTypes || []).indexOf(targetType) > -1;
  }

  editLayouts(): void {
    this.ngZone.run(() => {
      this.layoutResult = {data: false};
      const modalRef = this.angularModalService.show(LayoutSelectorModalComponent, {
        class: 'modal-lg',
        initialState: {currentLayouts: _.cloneDeep(this.editingModel.layouts)}
      });
      (modalRef.content as LayoutSelectorModalComponent).onSaved.subscribe((layouts: any[]) => {
        this.ngZone.run(() => {
          this.editingModel.layouts = _.cloneDeep(layouts);
          this.model.layouts = _.cloneDeep(layouts);
          this.markExternalFormDirty();
          if (!this.isNew) {
            this.model.savePartial('layouts').then((respObj: any) => {
              this.ngZone.run(() => {
                this.layoutResult.data = true;
                _.set(this.editingModel, 'validationWarnings', respObj.validationWarnings);
              });
            });
          }
        });
      });
    });
  }

  lineItemTotalPages(): number {
    if (!this.listPageSize || !this.listTotalCount) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.listTotalCount / this.listPageSize));
  }

  lineItemHasPagination(): boolean {
    return this.lineItemTotalPages() > 1;
  }

  changeLineItemPage(page: number): void {
    if (!this.lineItemProducts || page < 1 || page > this.lineItemTotalPages() || page === this.listCurrentPage) {
      return;
    }
    this.lineItemProducts.currentPage = page;
    this.lineItemProducts.changePage();
  }

  lineItemPageRangeStart(): number {
    if (!this.listTotalCount) {
      return 0;
    }
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  lineItemPageRangeEnd(): number {
    if (!this.listTotalCount) {
      return 0;
    }
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }
  isTeamSelected(teamId: string): boolean {
    return this.hasTeam(teamId);
  }

  trackById(_: number, item: any): string {
    return item.id;
  }

  readonly compareById = compareById;

  private resolveOwnerTeamReference(): void {
    if (!this.editingModel?.ownerTeam || !this.allPublisherTeams.length) {
      return;
    }
    const teamId = ownerTeamId(this.editingModel);
    const match = _.find(this.allPublisherTeams, ['id', teamId]);
    if (match) {
      this.editingModel.ownerTeam = match;
    }
  }

  private updateCanUpdateProduct(): void {
    const teamId = ownerTeamId(this.editingModel);
    this.canUpdateProduct = this.LocalUserPermissions.hasNetwork('MANAGE_SYSTEM')
      || this.LocalUserPermissions.hasNetwork('AX_PUBLISHER_ADMIN')
      || (!!teamId && this.LocalUserPermissions.has('AX_PUBLISHER_TEAM_ADMIN', teamId));
  }
}
